/** @prettier */

import {TextField, TextFieldProps, MenuItem} from '@mui/material';
import {useParams} from 'react-router-dom';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

//API import
import {
  getCompositionsGatewayByGatewayID,
  getCompositionsSLUnit,
  getCompositionsSLIFUnit,
  getCompositionsRTUnit,
  getCompositionsRTUnitByUnitID,
  getCompositionsMoubusUnitByOUID,
  getSettingUser,
} from '../API/API_Collection';
//interface import
import * as apiIF from '../Type/api';
//Style import
import '../css/common/common.css';

import {limit, pullDownMenu} from '../define';

type TextFieldSelectBoxPropsType = {
  /** 選択肢 */
  choices: ReadonlyArray<{value: string; label: string}>;
  /** 入力値応答 */
  setValue: React.Dispatch<React.SetStateAction<any>>; //意図的にanyにしている
  /** ID名 */
  id?: TextFieldProps['id'];
  /** テキストボックスラベル */
  label?: TextFieldProps['label'];
  /** 初期value値 */
  default?: TextFieldProps['defaultValue'];
  /** value値 */
  value?: TextFieldProps['value'];
  /** ネイティブセレクト */
  native?: boolean;
  /** ヘルパーテキスト */
  helperText?: TextFieldProps['helperText'];
  /** TextField種類（default:standard） */
  variant?: TextFieldProps['variant'];
  /** 有効無効 */
  disabled?: TextFieldProps['disabled'];
  /** マージン */
  margin?: TextFieldProps['margin'];
  /** 適用するクラス名 */
  class?: TextFieldProps['className'];
  /** プレースホルダ */
  placeholder?: TextFieldProps['placeholder'];
  /** メニューアイテムに適用するクラス名 */
  menuClass?: TextFieldProps['className'];
  /**(任意)入力欄に適用するプロパティ */
  InputProps?: TextFieldProps['InputProps']; //頭文字を小文字にすると別の引数になるため注意
  /** セレクト欄に適用するクラス名 */
  selectClass?: TextFieldProps['className'];
};
/**
 * テキストフィールドで作成したセレクトボックス
 * @param props props
 * @returns JSX
 */
const TextFieldSelectBox = (props: TextFieldSelectBoxPropsType) => {
  /**
   * 値選択時処理
   * @param event
   */
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    props.setValue(event.target.value);
  };

  const trans = useTranslation().t; //翻訳関数

  return (
    <TextField
      select={true}
      id={props.id}
      label={props.label}
      value={props.value}
      defaultValue={props.default}
      SelectProps={{
        //Card上でセレクトボックスを扱う際に選択欄がCardの後ろに隠れないように、
        //選択欄のzIndexをCardのzIndexより高い値に設定する
        MenuProps: {
          sx: {zIndex: 2100},
          classes: {
            list: props.selectClass,
          },
        },
        native: props.native,
      }}
      helperText={props.helperText}
      variant={props.variant === undefined ? 'standard' : props.variant}
      onChange={handleChange}
      disabled={props.disabled}
      margin={props.margin}
      placeholder={props.placeholder}
      className={props.class}
      InputProps={props.InputProps}>
      {props.choices.map(option => {
        if (props.native === true) {
          return (
            <option key={option.value} value={option.value}>
              {trans(`SelectBox.label.${option.label}`, {
                defaultValue: option.label,
              })}
            </option>
          );
        } else {
          return (
            <MenuItem
              key={option.value}
              value={option.value}
              className={props.menuClass}>
              {trans(`SelectBox.label.${option.label}`, {
                defaultValue: option.label,
              })}
            </MenuItem>
          );
        }
      })}
    </TextField>
  );
};

type SLOUAddressSelectBoxPropsType = {
  /** 入力値応答 */
  setAddress: TextFieldSelectBoxPropsType['setValue'];
  /** ラベル */
  label?: TextFieldSelectBoxPropsType['label'];
  /** 初期値 */
  default?: TextFieldSelectBoxPropsType['default'];
  /** 適用するクラス名 */
  class?: TextFieldSelectBoxPropsType['class'];
  /** ID名 */
  id?: TextFieldSelectBoxPropsType['id'];
};
/**
 * 室外機SLアドレスセレクトボックス
 * @param props props
 * @returns JSX
 */
const SLOUAddressSelectBox = (props: SLOUAddressSelectBoxPropsType) => {
  //パスパラメータ取得
  const {siteID, gwID, systemType} = useParams();

  const [choices, setChoices] = useState<{value: string; label: string}[]>([]); //室外機アドレス選択項目
  const [defaultValue, setDefaultValue] =
    useState<TextFieldSelectBoxPropsType['default']>(''); //選択肢の初期値

  const trans = useTranslation().t; //翻訳関数

  //室外機アドレス選択項目作成
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (
      siteID === undefined ||
      gwID === undefined ||
      systemType === undefined
    ) {
      return;
    }

    let promises = [];
    //室外機の情報を取得
    promises.push(getCompositionsSLUnit(siteID, gwID, {'max-result': 400}));
    //ゲートウェイの情報を取得
    promises.push(getCompositionsGatewayByGatewayID(siteID, gwID));

    Promise.all(promises).then(response => {
      const slUnitInfo = response[0] as apiIF.res.getCompositionsSLUnit;
      const gwInfo = response[1] as apiIF.res.getCompositionsGatewayByGatewayID;

      //登録済み室外機アドレス取得
      let registrationAddress: string[] = [];
      for (const ouInfo of slUnitInfo['outdoor-unit-informations']) {
        if (
          ouInfo['sl-system'] === systemType.slice(2) &&
          ouInfo['sl-address'] !== undefined
        ) {
          registrationAddress.push(ouInfo['sl-address']);
        }
      }

      //室外機アドレス選択項目作成
      let ouNumMax: number;
      if (gwInfo['sl-type'] === 'new') {
        ouNumMax = limit.REGISTRATION_NUM_NEW_SL_OU_MAX;
      } else {
        ouNumMax = limit.REGISTRATION_NUM_OLD_SL_OU_MAX;
      }

      let temp: {value: string; label: string}[] = [];
      for (let address = 0; address < ouNumMax; address++) {
        const addressString = String(address);

        //登録済みのアドレスの場合
        if (registrationAddress.includes(addressString)) {
          //編集しているSLアドレスのみ選択項目に含める
          if (addressString === props.default) {
            temp.push({
              value: addressString,
              label: addressString,
            });
          }
          continue;
        }
        temp.push({value: addressString, label: addressString});
      }
      setChoices(temp);

      //選択肢のデフォルトを設定
      //引数のデフォルトがない場合は選択肢の内、最初の要素を採用
      if (props.default === undefined) {
        props.setAddress(temp[0].value);
        setDefaultValue(temp[0].value);
      } else {
        setDefaultValue(props.default);
      }
    });
  }, [siteID, gwID, systemType, props.default]);

  if (choices.length === 0) {
    //処理中の場合
    return (
      <TextField
        disabled
        variant="standard"
        label={trans('SelectBox.loadingMsg')}
        className={props.class}
      />
    );
  }
  return (
    <TextFieldSelectBox
      label={props.label}
      choices={choices}
      setValue={props.setAddress}
      default={defaultValue}
      class={props.class}
      id={props.id}
    />
  );
};

type SLIUAddressSelectBoxPropsType = {
  /** 入力値応答 */
  setAddress: TextFieldSelectBoxPropsType['setValue'];
  /** ラベル */
  label?: TextFieldSelectBoxPropsType['label'];
  /** 初期値 */
  default?: TextFieldSelectBoxPropsType['default'];
  /** 適用するクラス名 */
  class?: TextFieldSelectBoxPropsType['class'];
  /** ID名 */
  id?: TextFieldSelectBoxPropsType['id'];
};
/**
 * 室内機SLアドレスセレクトボックス
 * @param props props
 * @returns JSX
 */
const SLIUAddressSelectBox = (props: SLIUAddressSelectBoxPropsType) => {
  //パスパラメータ取得
  const {siteID, gwID, systemType} = useParams();

  const [choices, setChoices] = useState<{value: string; label: string}[]>([]); //室内機アドレス選択項目
  const [defaultValue, setDefaultValue] =
    useState<TextFieldSelectBoxPropsType['default']>(''); //選択肢の初期値

  const trans = useTranslation().t; //翻訳関数

  //室内機アドレス選択項目作成
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (
      siteID === undefined ||
      gwID === undefined ||
      systemType === undefined
    ) {
      return;
    }

    let promises = [];
    //室外機の情報を取得
    promises.push(
      getCompositionsSLUnit(siteID, gwID, {
        'fields': 'sl-system,indoor-unit-informations',
        'max-result': 400,
      })
    );
    //その他空調機の情報を取得
    promises.push(
      getCompositionsSLIFUnit(siteID, gwID, {
        'fields': 'sl-system,sl-address',
        'max-result': 400,
      })
    );
    //ゲートウェイの情報を取得
    promises.push(getCompositionsGatewayByGatewayID(siteID, gwID));

    Promise.all(promises).then(response => {
      const slUnitInfo = response[0] as apiIF.res.getCompositionsSLUnit;
      const slIFUnitInfo = response[1] as apiIF.res.getCompositionsSLIFUnit;
      const gwInfo = response[2] as apiIF.res.getCompositionsGatewayByGatewayID;

      //登録済み室内機アドレス取得
      let registrationAddress: String[] = [];
      for (const ouInfo of slUnitInfo['outdoor-unit-informations']) {
        if (
          ouInfo['sl-system'] === systemType.slice(2) &&
          ouInfo['indoor-unit-informations'] !== undefined
        ) {
          for (const iuInfo of ouInfo['indoor-unit-informations']) {
            registrationAddress.push(iuInfo['sl-address']);
          }
        }
      }
      for (const unitInfo of slIFUnitInfo['unit-informations']) {
        if (
          unitInfo['sl-system'] === systemType.slice(2) &&
          unitInfo['sl-address'] !== undefined
        ) {
          registrationAddress.push(unitInfo['sl-address']);
        }
      }

      //室内機アドレス選択項目作成
      let iuNumMax: number;
      if (gwInfo['sl-type'] === 'new') {
        iuNumMax = limit.REGISTRATION_NUM_NEW_SL_IU_MAX;
      } else {
        iuNumMax = limit.REGISTRATION_NUM_OLD_SL_IU_MAX;
      }

      let temp: {value: string; label: string}[] = [];
      for (let address = 0; address < iuNumMax; address++) {
        const addressString = String(address);

        //登録済みのアドレスの場合
        if (registrationAddress.includes(addressString)) {
          //編集しているSLアドレスのみ選択項目に含める
          if (addressString === props.default) {
            temp.push({value: addressString, label: addressString});
          }
          continue;
        }
        temp.push({value: addressString, label: addressString});
      }
      setChoices(temp);

      //選択肢のデフォルトを設定
      //引数のデフォルトがない場合は選択肢の内、最初の要素を採用
      if (props.default === undefined) {
        props.setAddress(temp[0].value);
        setDefaultValue(temp[0].value);
      } else {
        setDefaultValue(props.default);
      }
    });
  }, [siteID, gwID, systemType, props.default]);

  if (choices.length === 0) {
    //処理中の場合
    return (
      <TextField
        disabled
        variant="standard"
        label={trans('SelectBox.loadingMsg')}
        className={props.class}
      />
    );
  }
  return (
    <TextFieldSelectBox
      label={props.label}
      choices={choices}
      setValue={props.setAddress}
      default={defaultValue}
      class={props.class}
      id={props.id}
    />
  );
};

type RTAddressSelectBoxPropsType = {
  /** 入力値応答 */
  setAddress: TextFieldSelectBoxPropsType['setValue'];
  /** ラベル */
  label?: TextFieldSelectBoxPropsType['label'];
  /** 初期値 */
  default?: TextFieldSelectBoxPropsType['default'];
  /** 適用するクラス名 */
  class?: TextFieldSelectBoxPropsType['class'];
  /** ID名 */
  id?: TextFieldSelectBoxPropsType['id'];
};
/**
 * 熱源機アドレスセレクトボックス
 * @param props props
 * @returns JSX
 */
const RTAddressSelectBox = (props: RTAddressSelectBoxPropsType) => {
  //パスパラメータ取得
  const {siteID, gwID, systemType} = useParams();

  const [choices, setChoices] = useState<{value: string; label: string}[]>([]); //熱源機アドレス選択項目
  const [defaultValue, setDefaultValue] =
    useState<TextFieldSelectBoxPropsType['default']>(''); //選択肢の初期値

  const trans = useTranslation().t; //翻訳関数

  useEffect(() => {
    //パスパラメータを取得できない場合
    if (
      siteID === undefined ||
      gwID === undefined ||
      systemType === undefined
    ) {
      return;
    }

    //熱源機の情報を取得
    getCompositionsRTUnit(siteID, gwID, {'max-result': 400}).then(response => {
      //登録済み室外機アドレス取得
      let registrationAddress: string[] = [];
      for (const unitInfo of response['unit-informations']) {
        if (
          unitInfo.system === systemType.slice(2) &&
          unitInfo.address !== undefined
        ) {
          registrationAddress.push(unitInfo.address);
        }
      }

      //熱源機アドレス選択項目作成
      let unitNumMin: number;
      let unitNumMax: number;
      if (systemType === 'rt1') {
        unitNumMin = 0;
        unitNumMax = 15;
      } else {
        unitNumMin = 16;
        unitNumMax = 31;
      }

      let temp: {value: string; label: string}[] = [];
      for (let address = unitNumMin; address <= unitNumMax; address++) {
        const addressString = String(address);

        //登録済みのアドレスの場合
        if (registrationAddress.includes(addressString)) {
          //編集しているSLアドレスのみ選択項目に含める
          if (addressString === props.default) {
            temp.push({value: addressString, label: addressString});
          }
          continue;
        }
        temp.push({value: addressString, label: addressString});
      }
      setChoices(temp);

      //選択肢のデフォルトを設定
      //引数のデフォルトがない場合は選択肢の内、最初の要素を採用
      if (props.default === undefined) {
        props.setAddress(temp[0].value);
        setDefaultValue(temp[0].value);
      } else {
        setDefaultValue(props.default);
      }
    });
  }, [siteID, gwID, systemType, props.default]);

  if (choices.length === 0) {
    //処理中の場合
    return (
      <TextField
        disabled
        variant="standard"
        label={trans('SelectBox.loadingMsg')}
        className={props.class}
      />
    );
  }
  return (
    <TextFieldSelectBox
      label={props.label}
      choices={choices}
      setValue={props.setAddress}
      default={defaultValue}
      class={props.class}
      id={props.id}
    />
  );
};

type RTIUAddressSelectBoxPropsType = {
  /** 入力値応答 */
  setAddress: TextFieldSelectBoxPropsType['setValue'];
  /** ラベル */
  label?: TextFieldSelectBoxPropsType['label'];
  /** 初期値 */
  default?: TextFieldSelectBoxPropsType['default'];
  /** 適用するクラス名 */
  class?: TextFieldSelectBoxPropsType['class'];
  /** ID名 */
  id?: TextFieldSelectBoxPropsType['id'];
};
/**
 * 室内機RTアドレスセレクトボックス
 * @param props props
 * @returns JSX
 */
const RTIUAddressSelectBox = (props: RTIUAddressSelectBoxPropsType) => {
  //パスパラメータ取得
  const {siteID, gwID, ouID} = useParams();

  const [choices, setChoices] = useState<{value: string; label: string}[]>([]); //室内機アドレス選択項目
  const [defaultValue, setDefaultValue] =
    useState<TextFieldSelectBoxPropsType['default']>(''); //選択肢の初期値

  const trans = useTranslation().t; //翻訳関数

  //室内機アドレス選択項目作成
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (siteID === undefined || gwID === undefined || ouID === undefined) {
      return;
    }

    getCompositionsRTUnitByUnitID(siteID, gwID, ouID).then(response => {
      //登録済み室内機アドレス取得
      const registrationAddress = response['indoor-unit-informations'].map(
        iuInfo => Number(iuInfo['address'])
      );

      //室内機アドレス選択項目作成
      let temp: {value: string; label: string}[] = [];
      for (
        let address = 1;
        address <= limit.REGISTRATION_NUM_RT_IU_MAX;
        address++
      ) {
        const addressString = String(address);

        //登録済みのアドレスの場合
        if (registrationAddress.includes(address)) {
          //編集しているSLアドレスのみ選択項目に含める
          if (addressString === props.default) {
            temp.push({value: addressString, label: addressString});
          }
          continue;
        }
        temp.push({value: addressString, label: addressString});
      }
      setChoices(temp);

      //選択肢のデフォルトを設定
      //引数のデフォルトがない場合は選択肢の内、最初の要素を採用
      if (props.default === undefined) {
        props.setAddress(temp[0].value);
        setDefaultValue(temp[0].value);
      } else {
        setDefaultValue(props.default);
      }
    });
  }, [siteID, gwID, ouID, props.default]);

  if (choices.length === 0) {
    //処理中の場合
    return (
      <TextField
        disabled
        variant="standard"
        label={trans('SelectBox.loadingMsg')}
        className={props.class}
      />
    );
  }
  return (
    <TextFieldSelectBox
      label={props.label}
      choices={choices}
      setValue={props.setAddress}
      default={defaultValue}
      class={props.class}
      id={props.id}
    />
  );
};

/**
 * Modbus室内機セレクトボックス
 * @param props props
 * @returns JSX
 */
type ModbusIUAddressSelectBoxPropsType = {
  /** 入力値応答 */
  setAddress: React.Dispatch<
    React.SetStateAction<'1' | '2' | '3' | '4' | '5' | '6' | '7' | '8'>
  >;
  /** ラベル */
  label?: TextFieldSelectBoxPropsType['label'];
  /** 初期値 */
  default?: '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8';
  /** 適用するクラス名 */
  class?: TextFieldSelectBoxPropsType['class'];
  /** ID名 */
  id?: TextFieldSelectBoxPropsType['id'];
};
const ModbusIUAddressSelectBox = (props: ModbusIUAddressSelectBoxPropsType) => {
  //パスパラメータ取得
  const {siteID, gwID, ouID} = useParams();

  const [choices, setChoices] = useState<{value: string; label: string}[]>([]); //室内機アドレス選択項目
  const [defaultValue, setDefaultValue] =
    useState<TextFieldSelectBoxPropsType['default']>(''); //選択肢の初期値

  const trans = useTranslation().t; //翻訳関数

  /**
   * ゲートウェイの情報とModbusの情報を取得
   */
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (siteID === undefined || gwID === undefined || ouID === undefined) {
      return;
    }

    getCompositionsMoubusUnitByOUID(siteID, gwID, ouID).then(response => {
      //登録済み室内機アドレス取得
      const registrationAddress = response['indoor-unit-informations'].map(
        iuInfo => Number(iuInfo['address'])
      );

      //室内機アドレス選択項目作成
      //Modbusは一台の室外機に8アドレスまで登録可能
      let temp: {value: string; label: string}[] = [];
      for (
        let address = 1;
        address <= limit.REGISTRATION_NUM_MODBUS_IU_MAX;
        address++
      ) {
        const addressString = String(address);

        //登録済みのアドレスの場合
        if (registrationAddress.includes(address)) {
          //編集しているSLアドレスのみ選択項目に含める
          if (addressString === props.default) {
            temp.push({value: addressString, label: addressString});
          }
          continue;
        }
        temp.push({value: addressString, label: addressString});
      }
      setChoices(temp);

      //選択肢のデフォルトを設定
      //引数のデフォルトがない場合は選択肢の内、最初の要素を採用
      if (props.default === undefined) {
        props.setAddress(
          temp[0].value as '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8'
        );
        setDefaultValue(temp[0].value);
      } else {
        setDefaultValue(props.default);
      }
    });
  }, [siteID, gwID, ouID, props.default]);

  if (choices.length === 0) {
    //処理中の場合
    return (
      <TextField
        disabled
        variant="standard"
        label={trans('SelectBox.loadingMsg')}
        className={props.class}
      />
    );
  }
  return (
    <TextFieldSelectBox
      label={props.label}
      choices={choices}
      setValue={
        props.setAddress as React.Dispatch<React.SetStateAction<string>>
      }
      //デフォルトがない場合は選択肢の内、最初の要素を採用
      default={props.default === undefined ? choices[0].value : props.default}
      class={props.class}
      id={props.id}
    />
  );
};

type VATNumberSelectBoxPropsType = {
  /** 選択肢 */
  choices: ReadonlyArray<{value: string; label: string}>;
  /** 入力値応答 */
  setValue: React.Dispatch<React.SetStateAction<any>>; //意図的にanyにしている
  /** ID名 */
  id?: TextFieldProps['id'];
  /** テキストボックスラベル */
  label?: TextFieldProps['label'];
  /** 初期value値 */
  default?: TextFieldProps['defaultValue'];
  /** value値 */
  value?: TextFieldProps['value'];
  /** ネイティブセレクト */
  native?: boolean;
  /** ヘルパーテキスト */
  helperText?: TextFieldProps['helperText'];
  /** TextField種類（default:standard） */
  variant?: TextFieldProps['variant'];
  /** 有効無効 */
  disabled?: TextFieldProps['disabled'];
  /** マージン */
  margin?: TextFieldProps['margin'];
  /** 適用するクラス名 */
  class?: TextFieldProps['className'];
  /** プレースホルダ */
  placeholder?: TextFieldProps['placeholder'];
  /** メニューアイテムに適用するクラス名 */
  menuClass?: TextFieldProps['className'];
  /**(任意)入力欄に適用するプロパティ */
  InputProps?: TextFieldProps['InputProps']; //頭文字を小文字にすると別の引数になるため注意
  /** セレクト欄に適用するクラス名 */
  selectClass?: TextFieldProps['className'];
};
/**
 * VAT番号のフォーマット選択セレクトボックス
 * @param props props
 * @returns JSX
 */
const VATNumberSelectBox = (props: VATNumberSelectBoxPropsType) => {
  const trans = useTranslation().t; //翻訳関数

  //prettier-ignore
  const [userCountry, setUserCountry] = useState<apiIF.res.getSettingUser['country']>(''); //ユーザの所在国

  /** 国→VAT型式変換テーブル */
  const countryToVATConvertTable = [
    {country: 'belgium', format: 'be_vat'},
    {country: 'bulgaria', format: 'bg_uic'},
    {country: 'bulgaria', format: 'bg_vat'},
    {country: 'cyprus', format: 'cy_vat'},
    {country: 'germany', format: 'de_stn'},
    {country: 'germany', format: 'de_vat'},
    {country: 'denmark', format: 'dk_vat'},
    {country: 'estonia', format: 'ee_vat'},
    {country: 'spain', format: 'es_cif'},
    {country: 'spain', format: 'es_vat'},
    {country: 'finland', format: 'fi_vat'},
    {country: 'france', format: 'fr_vat'},
    {country: 'uk', format: 'gb_vat'},
    {country: 'greece', format: 'gr_vat'},
    {country: 'croatia', format: 'hr_vat'},
    {country: 'hungary', format: 'hu_tin'},
    {country: 'hungary', format: 'hu_vat'},
    {country: 'italy', format: 'it_vat'},
    {country: 'lithuania', format: 'lt_vat'},
    {country: 'netherlands', format: 'nl_vat'},
    {country: 'norway', format: 'no_vat'},
    {country: 'norway', format: 'no_voec'},
    {country: 'poland', format: 'pl_vat'},
    {country: 'portugal', format: 'pt_vat'},
    {country: 'romania', format: 'ro_tin'},
    {country: 'romania', format: 'ro_vat'},
    {country: 'sweden', format: 'se_vat'},
    {country: 'uk', format: 'xi_vat'},
    {country: 'ireland', format: 'ie_vat'},
    {country: 'austria', format: 'at_vat'},
    {country: 'switzerland', format: 'ch_uid'},
    {country: 'switzerland', format: 'ch_vat'},
    {country: 'slovakia', format: 'sk_vat'},
    {country: 'slovenia', format: 'si_tin'},
    {country: 'slovenia', format: 'si_vat'},
    {country: 'czechRepublic', format: 'cz_vat'},
    {country: 'malta', format: 'mt_vat'},
    {country: 'latvia', format: 'lv_vat'},
    {country: 'liechtenstein', format: 'li_uid'},
    {country: 'luxembourg', format: 'lu_vat'},
  ];

  //ユーザ情報の取得
  useEffect(() => {
    getSettingUser().then(info => {
      setUserCountry(info.country);
    });
  }, []);

  /**
   * 値選択時処理
   * @param event
   */
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    props.setValue(event.target.value);
  };

  //処理中の場合
  if (userCountry === '') {
    return (
      <TextField
        disabled
        variant="standard"
        label={trans('SelectBox.loadingMsg')}
        className={props.class}
      />
    );
  }
  /** ユーザ所在国と一致するフォーマット */
  const filterFormat = countryToVATConvertTable.filter(data => {
    return data.country === userCountry;
  });
  /** 選択肢 */
  const countryChoice = pullDownMenu.VAT_NUMBER_FORMAT.filter(pullDown => {
    return filterFormat.some(data => {
      return pullDown.value === data.format;
    });
  });

  return (
    <TextField
      select={true}
      id={props.id}
      label={props.label}
      value={props.value}
      defaultValue={props.default}
      SelectProps={{
        //Card上でセレクトボックスを扱う際に選択欄がCardの後ろに隠れないように、
        //選択欄のzIndexをCardのzIndexより高い値に設定する
        MenuProps: {
          sx: {zIndex: 2100},
          classes: {
            list: props.selectClass,
          },
        },
        native: props.native,
      }}
      helperText={props.helperText}
      variant={props.variant === undefined ? 'standard' : props.variant}
      onChange={handleChange}
      disabled={props.disabled}
      margin={props.margin}
      placeholder={props.placeholder}
      className={props.class}
      InputProps={props.InputProps}>
      {countryChoice.map(option => {
        if (props.native === true) {
          return (
            <option key={option.value} value={option.value}>
              {trans(`SelectBox.label.${option.label}`, {
                defaultValue: option.label,
              })}
            </option>
          );
        }
        return (
          <MenuItem
            key={option.value}
            value={option.value}
            className={props.menuClass}>
            {trans(`SelectBox.label.${option.label}`, {
              defaultValue: option.label,
            })}
          </MenuItem>
        );
      })}
    </TextField>
  );
};

export default TextFieldSelectBox;
export {
  SLOUAddressSelectBox,
  SLIUAddressSelectBox,
  RTAddressSelectBox,
  RTIUAddressSelectBox,
  ModbusIUAddressSelectBox,
  VATNumberSelectBox,
};
